// filepath: admin-dashboard/src/services/auth/authService.ts
import axios from 'axios';
import { getEnvironmentConfig } from '../../utils/envConfig';

const config = getEnvironmentConfig();
const API_BASE_URL = config.apiBaseUrl;
const TOKEN_STORAGE_KEY = 'admiral_admin_token';
const TOKEN_EXPIRY_KEY = 'admiral_admin_token_expiry';
const USER_STORAGE_KEY = 'admiral_admin_user';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: UserProfile;
}

export interface UserProfile {
  username: string;
  role: 'admin' | 'editor';
  must_change_password: boolean;
}

export interface CreateUserPayload {
  username: string;
  password: string;
  role: 'admin' | 'editor';
}

export interface UpdateUserPayload {
  role?: 'admin' | 'editor';
  is_active?: boolean;
  must_change_password?: boolean;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface ResetPasswordPayload {
  new_password: string;
}

export interface UserListItem {
  username: string;
  role: 'admin' | 'editor';
  is_active: boolean;
  must_change_password: boolean;
}

class AuthService {
  private token: string | null = null;
  private tokenExpiry: number | null = null;
  private user: UserProfile | null = null;

  constructor() {
    this.loadTokenFromStorage();
  }

  private loadTokenFromStorage(): void {
    try {
      this.token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      this.tokenExpiry = expiry ? parseInt(expiry, 10) : null;

      const userJson = localStorage.getItem(USER_STORAGE_KEY);
      this.user = userJson ? JSON.parse(userJson) : null;

      // Check if token is expired
      if (this.tokenExpiry && Date.now() > this.tokenExpiry) {
        this.clearToken();
      }
    } catch (error) {
      console.warn('Failed to load token from storage:', error);
      this.clearToken();
    }
  }

  private saveTokenToStorage(
    token: string,
    expiresIn: number,
    user: UserProfile
  ): void {
    try {
      const expiryTime = Date.now() + expiresIn * 1000;
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      this.token = token;
      this.tokenExpiry = expiryTime;
      this.user = user;
    } catch (error) {
      console.warn('Failed to save token to storage:', error);
    }
  }

  private clearToken(): void {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear token from storage:', error);
    }
    this.token = null;
    this.tokenExpiry = null;
    this.user = null;
  }

  public async login(credentials: LoginCredentials): Promise<UserProfile> {
    const response = await axios.post<TokenResponse>(
      `${API_BASE_URL}/auth/login`,
      credentials,
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { access_token, expires_in, user } = response.data;
    this.saveTokenToStorage(access_token, expires_in, user);
    return user;
  }

  public logout(): void {
    this.clearToken();
  }

  public getToken(): string | null {
    if (this.tokenExpiry && Date.now() > this.tokenExpiry) {
      this.clearToken();
      return null;
    }
    return this.token;
  }

  public getUser(): UserProfile | null {
    if (!this.getToken()) return null;
    return this.user;
  }

  public isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  public isAdmin(): boolean {
    return this.user?.role === 'admin';
  }

  public mustChangePassword(): boolean {
    return this.user?.must_change_password ?? false;
  }

  /**
   * Mark that the user has changed their password so the UI can stop
   * redirecting to the change-password page.
   */
  public clearMustChangePassword(): void {
    if (this.user) {
      this.user = { ...this.user, must_change_password: false };
      try {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(this.user));
      } catch {
        // ignore
      }
    }
  }

  // --- User management (admin) ---

  private authHeaders() {
    return { Authorization: `Bearer ${this.getToken()}` };
  }

  public async listUsers(): Promise<UserListItem[]> {
    const res = await axios.get<{ users: UserListItem[] }>(
      `${API_BASE_URL}/auth/users`,
      { headers: this.authHeaders() }
    );
    return res.data.users;
  }

  public async createUser(payload: CreateUserPayload): Promise<UserListItem> {
    const res = await axios.post<{ user: UserListItem }>(
      `${API_BASE_URL}/auth/users`,
      payload,
      { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
    );
    return res.data.user;
  }

  public async updateUser(
    username: string,
    payload: UpdateUserPayload
  ): Promise<UserListItem> {
    const res = await axios.put<{ user: UserListItem }>(
      `${API_BASE_URL}/auth/users/${username}`,
      payload,
      { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
    );
    return res.data.user;
  }

  public async deleteUser(username: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/auth/users/${username}`, {
      headers: this.authHeaders(),
    });
  }

  public async resetUserPassword(
    username: string,
    payload: ResetPasswordPayload
  ): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/auth/users/${username}/reset-password`,
      payload,
      { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
    );
  }

  public async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await axios.post(`${API_BASE_URL}/auth/change-password`, payload, {
      headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
    });
    this.clearMustChangePassword();
  }

  public async getPasswordRequirements(): Promise<string[]> {
    const res = await axios.get<{ requirements: string[] }>(
      `${API_BASE_URL}/auth/password-requirements`
    );
    return res.data.requirements;
  }
}

export const authService = new AuthService();
